/**
 * AdminSidebar — Enterprise Workspace navigatie.
 *
 * Layout:
 *   [Logo/brand]
 *   [User avatar + rol-badge]
 *   ─────────────
 *   ⭐ Favorieten     (dynamisch)
 *   ─────────────
 *   [Overzicht → Dashboard]
 *   [Verkoop ▾]
 *      Offertes, Bestellingen, Leads
 *   [Logistiek ▾]
 *      Pickup & Delivery, Consolidaties, Status Generator
 *   ...
 *   ─────────────
 *   [Uitloggen]
 *
 * Interacties:
 *   - Groepskop klikbaar → inklap/uitklap (chevron rotates)
 *   - Item hover → subtle white/5 bg + text-white
 *   - Item active → brand-500/30 bg + linker rand-indicator
 *   - Hover op item toont een pin-icoon rechts → favoriet toggle
 *
 * State-persistence: expanded_groups en sidebar_collapsed komen uit
 * user_preferences (via UserPreferencesContext).
 */
import React, { useState, useEffect } from "react";
import { ChevronDown, Star, LogOut, Lock, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { NAV_GROUPS, NAV_ITEMS_FLAT } from "./nav-config";
import { useAuth, ROLE_LABELS } from "../../../context/AuthContext";
import { useUserPreferences } from "../../../context/UserPreferencesContext";
import { useReminderAlerts } from "../ReminderAlertProvider";

const DEFAULT_EXPANDED = ["overzicht", "verkoop", "financieel", "communicatie"];

export default function AdminSidebar({ active, setActive, onLogout, mobileOpen, setMobileOpen }) {
  const { user, hasPermission } = useAuth();
  const { prefs, patchPrefs, toggleFavorite, isFavorite } = useUserPreferences();
  const { openCount, overdueCount, openModal } = useReminderAlerts();

  // Local mirror voor snelle groepklap-response — pushed naar backend bij verandering.
  const [expanded, setExpanded] = useState(() => {
    return new Set(prefs?.navigation_prefs?.expanded_groups || DEFAULT_EXPANDED);
  });

  useEffect(() => {
    if (prefs?.navigation_prefs?.expanded_groups) {
      setExpanded(new Set(prefs.navigation_prefs.expanded_groups));
    }
  }, [prefs?.navigation_prefs?.expanded_groups]);

  const toggleGroup = (groupKey) => {
    const next = new Set(expanded);
    if (next.has(groupKey)) next.delete(groupKey);
    else next.add(groupKey);
    setExpanded(next);
    patchPrefs({ navigation_prefs: { expanded_groups: Array.from(next) } });
  };

  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => {
      if (hasPermission(it.perm)) return true;
      if (it.key === "instellingen" && user?.role === "manager") return true;
      return false;
    }),
  })).filter((g) => g.items.length > 0);

  const favItems = (prefs.favorites || [])
    // Sprint Fase A Afronding: favorites zijn multi-namespace geworden.
    // In de sidebar tonen we uitsluitend menu-item favs (bare keys).
    // Widget/command/entity favs worden op hun eigen plek (dashboard,
    // command-palette, entity-picker) getoond.
    .filter((k) => typeof k === "string" && !k.includes(":"))
    .map((k) => NAV_ITEMS_FLAT[k]?.item)
    .filter(Boolean);

  const handleItemClick = (key) => {
    if (key === "reminders" && openCount > 0 && key !== active) {
      openModal();
      setMobileOpen?.(false);
      return;
    }
    setActive(key);
    setMobileOpen?.(false);
  };

  return (
    <aside
      className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 inset-y-0 left-0 z-40 w-[260px] h-screen bg-brand-900 text-white flex flex-col shadow-2xl transform transition-transform duration-200`}
      data-testid="admin-sidebar"
      aria-label="Backoffice navigatie"
    >
      {/* Brand + User */}
      <div className="p-4 border-b border-white/5 flex-shrink-0">
        <div className="text-[10px] tracking-[0.24em] uppercase text-brand-300 font-chivo font-bold">
          / 5999Cargo
        </div>
        <div className="font-chivo font-black text-lg mt-0.5 leading-tight">Backoffice</div>
        {user && (
          <div className="mt-3 flex items-start gap-2.5" data-testid="admin-current-user">
            <div className="w-8 h-8 rounded-full bg-brand-500/30 border border-brand-500/60 flex items-center justify-center text-[11px] font-bold text-brand-100 flex-shrink-0">
              {(user.name || user.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-chivo font-bold text-white text-[13px] truncate leading-tight">{user.name || user.email}</div>
              <div className="text-[10px] text-slate-400 truncate">{user.email}</div>
              <div className="mt-1 inline-block bg-brand-500/20 border border-brand-500/40 px-1.5 py-0.5 text-[9px] tracking-[0.12em] uppercase rounded-full text-brand-100">
                {ROLE_LABELS[user.role] || user.role}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation body */}
      <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent" data-testid="admin-nav">
        {/* Favorieten */}
        {favItems.length > 0 && (
          <div className="mb-2">
            <GroupHeader label="Favorieten" icon={Star} solid />
            <ul className="space-y-0.5 px-2">
              {favItems.map((it) => (
                <NavItemButton
                  key={`fav-${it.key}`}
                  item={it}
                  active={active === it.key}
                  onClick={() => handleItemClick(it.key)}
                  reminderCount={it.key === "reminders" ? openCount : 0}
                  overdueCount={it.key === "reminders" ? overdueCount : 0}
                  favorite
                  onToggleFav={() => toggleFavorite(it.key)}
                  isFav={true}
                />
              ))}
            </ul>
          </div>
        )}

        {/* Groepen */}
        {visibleGroups.map((g) => {
          const isExpanded = expanded.has(g.key) || g.key === "overzicht"; // overzicht = altijd open
          const isSingleItem = g.items.length === 1 && g.key === "overzicht";
          if (isSingleItem) {
            const it = g.items[0];
            return (
              <ul key={g.key} className="space-y-0.5 px-2 mb-1">
                <NavItemButton
                  item={it}
                  active={active === it.key}
                  onClick={() => handleItemClick(it.key)}
                  reminderCount={it.key === "reminders" ? openCount : 0}
                  overdueCount={it.key === "reminders" ? overdueCount : 0}
                  onToggleFav={() => toggleFavorite(it.key)}
                  isFav={isFavorite(it.key)}
                />
              </ul>
            );
          }
          return (
            <div key={g.key} className="mb-0.5">
              <GroupHeader
                label={g.label}
                expanded={isExpanded}
                onClick={() => toggleGroup(g.key)}
                testid={`sidebar-group-${g.key}`}
              />
              {isExpanded && (
                <ul className="space-y-0.5 px-2 mb-1">
                  {g.items.map((it) => (
                    <NavItemButton
                      key={it.key}
                      item={it}
                      active={active === it.key}
                      onClick={() => handleItemClick(it.key)}
                      reminderCount={it.key === "reminders" ? openCount : 0}
                      overdueCount={it.key === "reminders" ? overdueCount : 0}
                      onToggleFav={() => toggleFavorite(it.key)}
                      isFav={isFavorite(it.key)}
                    />
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        {visibleGroups.length === 0 && (
          <div className="px-4 py-6 text-xs text-slate-400 flex items-start gap-2">
            <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            Je hebt geen toegang tot modules.
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 flex-shrink-0">
        <button
          type="button"
          onClick={onLogout}
          className="w-full flex items-center justify-center gap-2 bg-transparent border border-white/20 text-slate-300 hover:bg-white/5 hover:text-white rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-150"
          data-testid="admin-logout-button"
        >
          <LogOut className="w-3.5 h-3.5" />
          Uitloggen
        </button>
      </div>
    </aside>
  );
}

function GroupHeader({ label, expanded, onClick, icon: Icon, solid, testid }) {
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`w-full flex items-center gap-1.5 px-4 py-2 mt-2 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors duration-150 ${
        solid ? "text-brand-300" : "text-slate-500 hover:text-slate-300"
      } ${clickable ? "cursor-pointer" : "cursor-default"}`}
      data-testid={testid}
    >
      {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}
      <span className="flex-1 text-left">{label}</span>
      {clickable && (
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? "rotate-0" : "-rotate-90"}`}
        />
      )}
    </button>
  );
}

function NavItemButton({ item, active, onClick, reminderCount = 0, overdueCount = 0, onToggleFav, isFav, favorite }) {
  const Icon = item.icon;
  return (
    <li className="relative group">
      <button
        type="button"
        onClick={onClick}
        data-testid={`admin-nav-${item.key}`}
        className={`w-full flex items-center gap-2.5 pl-3 pr-2 py-2 rounded-md text-[13px] font-medium transition-colors duration-150 relative ${
          active
            ? "bg-brand-500/25 text-white before:content-[''] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:bg-brand-500 before:rounded-r"
            : "text-slate-300 hover:bg-white/5 hover:text-white"
        }`}
      >
        <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
        <span className="text-left flex-1 truncate">{item.label}</span>
        {reminderCount > 0 && (
          <span
            className={`inline-flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] ${
              overdueCount > 0
                ? "bg-red-600 text-white animate-pulse"
                : "bg-amber-400 text-brand-900"
            }`}
            data-testid="admin-nav-reminders-badge"
            title={`${reminderCount} open${overdueCount > 0 ? ` · ${overdueCount} te laat` : ""}`}
          >
            {reminderCount > 99 ? "99+" : reminderCount}
          </span>
        )}
      </button>
      {onToggleFav && !favorite && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 hover:text-amber-400 transition-all duration-150 text-slate-500 p-1"
          aria-label={isFav ? "Uit favorieten verwijderen" : "Aan favorieten toevoegen"}
          data-testid={`admin-nav-fav-${item.key}`}
          title="Toevoegen aan favorieten"
        >
          <Star className={`w-3.5 h-3.5 ${isFav ? "fill-amber-400 text-amber-400 opacity-100" : ""}`} />
        </button>
      )}
      {favorite && onToggleFav && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-amber-400 transition-opacity duration-150 p-1"
          aria-label="Uit favorieten verwijderen"
          data-testid={`admin-nav-unfav-${item.key}`}
          title="Verwijderen uit favorieten"
        >
          <Star className="w-3.5 h-3.5 fill-amber-400" />
        </button>
      )}
    </li>
  );
}
